import * as React from "react";
import { FileCheck2, Plus } from "lucide-react";
import type { DocumentScanDraft, IncomeSummary } from "@/types/tax";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dropdown, type DropdownOption } from "@/components/ui/Dropdown";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { PdfPreview } from "@/components/domain/PdfPreview";
import { gstFromGross } from "@/lib/gst";
import { currency, makeId, parseMoney } from "@/lib/utils";

type IncomeProvider = IncomeSummary["provider"];

const providerOptions: DropdownOption<IncomeProvider>[] = [
  { value: "uber", label: "Uber" },
  { value: "didi", label: "Didi" },
  { value: "other", label: "Other rideshare" },
  { value: "manual", label: "Manual summary" },
];

interface IncomeFormProps {
  userId: string;
  draft?: DocumentScanDraft | null;
  editingIncome?: IncomeSummary | null;
  onSave: (income: IncomeSummary) => void;
  onCancelEdit?: () => void;
}

export function IncomeForm({ userId, draft, editingIncome, onSave, onCancelEdit }: IncomeFormProps) {
  const [provider, setProvider] = React.useState<IncomeProvider>("manual");
  const [month, setMonth] = React.useState(new Date().toISOString().slice(0, 7));
  const [grossIncome, setGrossIncome] = React.useState("");
  const [platformFeesGross, setPlatformFeesGross] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [showNotes, setShowNotes] = React.useState(false);
  const [scanMeta, setScanMeta] = React.useState<{ sourceDocumentId: string; confidence: IncomeSummary["confidence"]; fileName: string } | null>(null);

  React.useEffect(() => {
    if (!draft) return;
    const income = draft.income || {};
    setProvider((income.provider as IncomeProvider) || "other");
    if (income.periodStart) setMonth(String(income.periodStart).slice(0, 7));
    setGrossIncome(moneyInput(income.grossIncome));
    setPlatformFeesGross(moneyInput(income.platformFeesGross));
    setNotes(String(income.notes || ""));
    setShowNotes(Boolean(income.notes));
    setScanMeta({ sourceDocumentId: draft.sourceDocumentId, confidence: draft.confidence, fileName: draft.fileName });
  }, [draft]);

  React.useEffect(() => {
    if (!editingIncome) return;
    setProvider(editingIncome.provider);
    if (editingIncome.periodStart) setMonth(editingIncome.periodStart.slice(0, 7));
    setGrossIncome(moneyInput(editingIncome.grossIncome));
    setPlatformFeesGross(moneyInput(editingIncome.platformFeesGross));
    setNotes(editingIncome.notes || "");
    setShowNotes(Boolean(editingIncome.notes));
    setScanMeta(
      editingIncome.sourceType === "document_scan" && editingIncome.sourceDocumentId
        ? {
            sourceDocumentId: editingIncome.sourceDocumentId,
            confidence: editingIncome.confidence || "medium",
            fileName: "Existing scanned source",
          }
        : null,
    );
  }, [editingIncome]);

  const gross = parseMoney(grossIncome);
  const deductions = parseMoney(platformFeesGross);
  const deductionsGst = deductions > 0 ? parseMoney((deductions / 11).toFixed(2)) : 0;

  function resetForm() {
    setProvider("manual");
    setMonth(new Date().toISOString().slice(0, 7));
    setGrossIncome("");
    setPlatformFeesGross("");
    setNotes("");
    setShowNotes(false);
    setScanMeta(null);
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (gross <= 0) return;
    const { periodStart, periodEnd } = monthToPeriod(month);
    onSave({
      id: editingIncome?.id || makeId("inc"),
      userId,
      provider,
      periodStart,
      periodEnd,
      grossIncome: gross,
      platformFeesGross: deductions,
      platformFeesGst: deductionsGst,
      tollsGross: 0,
      tipsAndBonuses: 0,
      sourceType: scanMeta ? "document_scan" : editingIncome?.sourceType || "manual",
      sourceDocumentId: scanMeta?.sourceDocumentId || editingIncome?.sourceDocumentId || null,
      confidence: scanMeta?.confidence || editingIncome?.confidence || "high",
      notes,
    });
    resetForm();
  }

  return (
    <Card>
      <CardHeader className="gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <CardTitle>{editingIncome ? "Edit income" : "Monthly income"}</CardTitle>
          <CardDescription>Free and Pro users can enter monthly income and platform fees for GST.</CardDescription>
        </div>
        <Badge variant={scanMeta ? "credit" : "outline"}>{scanMeta ? "PDF draft" : "Free"}</Badge>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-5" onSubmit={submit}>
          {scanMeta ? (
            <div className="grid gap-3 rounded-lg border border-border bg-surface p-3">
              <div className="flex items-start gap-3">
                <FileCheck2 className="mt-0.5 size-5 shrink-0 text-credit" aria-hidden="true" />
                <div>
                  <p className="font-semibold text-ink">Scanned draft loaded</p>
                  <p className="mt-1 text-sm text-muted">Review the extracted fields against the source file, then add income.</p>
                </div>
              </div>
              <PdfPreview documentId={scanMeta.sourceDocumentId} fileName={scanMeta.fileName} display="compact" />
            </div>
          ) : null}
          <FieldGroup className="grid gap-4 md:grid-cols-2">
            <Field>
              <FieldLabel>Provider</FieldLabel>
              <Dropdown label="Income provider" value={provider} options={providerOptions} onChange={setProvider} />
            </Field>
            <Field>
              <FieldLabel htmlFor="income-month">Month</FieldLabel>
              <Input id="income-month" type="month" value={month} onChange={(event) => setMonth(event.target.value)} />
            </Field>
            <MoneyField id="gross-income" label="Income" value={grossIncome} onChange={setGrossIncome} />
            <MoneyField id="platform-fees-gross" label="Deductions" value={platformFeesGross} onChange={setPlatformFeesGross} />
            <div className="md:col-span-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => setShowNotes((current) => !current)}>
                {showNotes ? "Hide notes" : "More details"}
              </Button>
            </div>
            {showNotes ? (
              <Field className="md:col-span-2">
                <FieldLabel htmlFor="income-notes">Notes</FieldLabel>
                <Textarea id="income-notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
              </Field>
            ) : null}
          </FieldGroup>

          <div className="grid gap-3 rounded-lg border border-border bg-surface p-3 sm:grid-cols-3">
            <Preview label="G1 sales" value={currency(gross)} />
            <Preview label="1A GST on sales" value={currency(gstFromGross(gross))} tone="payable" />
            <Preview label="1B deductions GST" value={currency(deductionsGst)} tone="credit" />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="submit" disabled={gross <= 0}>
              <Plus className="size-4" aria-hidden="true" />
              {editingIncome ? "Update income" : "Add income"}
            </Button>
            {editingIncome ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  resetForm();
                  onCancelEdit?.();
                }}
              >
                Cancel
              </Button>
            ) : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function monthToPeriod(month: string) {
  const [yearText, monthText] = month.split("-");
  const year = Number(yearText) || new Date().getFullYear();
  const monthIndex = (Number(monthText) || 1) - 1;
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  return {
    periodStart: toIso(start),
    periodEnd: toIso(end),
  };
}

function toIso(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function moneyInput(value: unknown) {
  const amount = parseMoney(typeof value === "number" ? value : String(value || ""));
  return amount > 0 ? String(amount) : "";
}

function MoneyField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input id={id} inputMode="decimal" value={value} onChange={(event) => onChange(event.target.value)} placeholder="0.00" />
      <FieldDescription>Use Australian dollar amounts.</FieldDescription>
    </Field>
  );
}

function Preview({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "credit" | "payable" }) {
  const toneClass = tone === "credit" ? "text-credit" : tone === "payable" ? "text-payable" : "text-ink";
  return (
    <div>
      <p className="text-xs font-semibold text-muted">{label}</p>
      <p className={`tabular mt-1 font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}
